import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { Modal, IconArrowHead, H4 } from '../../../components';

import ZoomableImage from './ZoomableImage';
import css from './MobileImageModal.module.css';

/**
 * MobileImageModal - A mobile-optimized modal for viewing images with zoom and navigation
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className - Custom class that extends the default class
 * @param {string?} props.rootClassName - Custom class that overrides the default class
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Array<propTypes.image>} props.images - Array of image objects from SDK
 * @param {Array<string>} props.imageVariants - Image variant names to use
 * @param {number?} props.initialIndex - Initial image index to display (default: 0)
 * @param {Function} props.onManageDisableScrolling - Redux action to manage scroll locking
 * @returns {JSX.Element} mobile image modal component
 */
const MobileImageModal = props => {
  const {
    className,
    rootClassName,
    isOpen,
    onClose,
    images,
    imageVariants,
    initialIndex = 0,
    onManageDisableScrolling,
  } = props;

  const intl = useIntl();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Reset to initial index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setZoomLevel(1);
    }
  }, [isOpen, initialIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = e => {
      if (!isOpen) return;

      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, currentIndex, images.length]);

  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setZoomLevel(1); // Reset zoom when changing images
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setZoomLevel(1); // Reset zoom when changing images
    }
  };

  const handleZoomChange = newZoom => {
    setZoomLevel(newZoom);
  };

  if (!images || images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];
  const isFirstImage = currentIndex === 0;
  const isLastImage = currentIndex === images.length - 1;
  const showNavigation = images.length > 1;

  const imageAlt = intl.formatMessage(
    { id: 'MobileImageModal.imageAltText' },
    { index: currentIndex + 1, count: images.length }
  );

  const classes = classNames(rootClassName || css.root, className);

  return (
    <Modal
      id="MobileImageModal"
      containerClassName={css.modalContainer}
      scrollLayerClassName={css.modalScrollLayer}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      lightCloseButton
    >
      <div className={classes}>
        {/* Image Counter */}
        {showNavigation && (
          <div className={css.imageCounter}>
            <H4 as="span" className={css.imageCounterText}>
              {currentIndex + 1}/{images.length}
            </H4>
          </div>
        )}

        {/* Previous Arrow */}
        {showNavigation && (
          <button
            className={classNames(css.navButton, css.navLeft, {
              [css.navButtonDisabled]: isFirstImage,
            })}
            onClick={handlePrevious}
            disabled={isFirstImage}
            aria-label={intl.formatMessage({ id: 'MobileImageModal.previousImage' })}
            type="button"
          >
            <div className={css.navArrowWrapper}>
              <IconArrowHead direction="left" size="big" className={css.arrowIcon} />
            </div>
          </button>
        )}

        {/* Zoomable Image */}
        <ZoomableImage
          className={css.zoomableImage}
          image={currentImage}
          imageVariants={imageVariants}
          alt={imageAlt}
          onZoomChange={handleZoomChange}
        />

        {/* Next Arrow */}
        {showNavigation && (
          <button
            className={classNames(css.navButton, css.navRight, {
              [css.navButtonDisabled]: isLastImage,
            })}
            onClick={handleNext}
            disabled={isLastImage}
            aria-label={intl.formatMessage({ id: 'MobileImageModal.nextImage' })}
            type="button"
          >
            <div className={css.navArrowWrapper}>
              <IconArrowHead direction="right" size="big" className={css.arrowIcon} />
            </div>
          </button>
        )}
      </div>
    </Modal>
  );
};

export default MobileImageModal;
